/**
 * React Query hooks for analytics event tracking.
 *
 * Provides fire-and-forget event tracking that doesn't block the UI.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useCallback, useEffect, useRef } from 'react';
import { mlbApi } from '../services/api';
import { STALE_TIMES, QUERY_RETRY_CONFIG } from '../types/statcast';
import {
  AnalyticsEventType,
  type EventType,
  type AnalyticsOverviewResponse,
} from '../types/analytics';

// Re-export for convenience
export { AnalyticsEventType };
export type { EventType };

// =============================================================================
// Session ID Management
// =============================================================================

/**
 * Generate or retrieve session ID from sessionStorage.
 * Session ID persists for the browser session (until tab/window closes).
 */
const getSessionId = (): string => {
  const key = 'analytics_session_id';
  let sessionId = sessionStorage.getItem(key);

  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem(key, sessionId);
  }

  return sessionId;
};

/**
 * Detect device type from user agent.
 */
const getDeviceType = (): 'desktop' | 'mobile' | 'tablet' => {
  const ua = navigator.userAgent.toLowerCase();
  if (/mobile|android|iphone/.test(ua)) return 'mobile';
  if (/tablet|ipad/.test(ua)) return 'tablet';
  return 'desktop';
};

// =============================================================================
// Event Tracking Hooks
// =============================================================================

interface TrackEventParams {
  eventType: EventType;
  eventSubtype?: string;
  pagePath?: string;
  referrerPath?: string;
  targetType?: string;
  targetId?: number;
  eventData?: Record<string, unknown>;
}

/**
 * Hook for tracking analytics events.
 *
 * Events are sent asynchronously without blocking the UI.
 * Errors are silently ignored to prevent analytics from affecting UX.
 */
export function useTrackEvent() {
  const { getToken } = useAuth();
  const sessionId = getSessionId();
  const lastPageRef = useRef<string | null>(null);

  const mutation = useMutation({
    mutationFn: async (params: TrackEventParams) => {
      const token = await getToken();
      return mlbApi.trackAnalyticsEvent(
        {
          event_type: params.eventType,
          event_subtype: params.eventSubtype,
          session_id: sessionId,
          page_path: params.pagePath || window.location.pathname,
          referrer_path: params.referrerPath || lastPageRef.current || undefined,
          target_type: params.targetType,
          target_id: params.targetId,
          event_data: params.eventData,
          device_type: getDeviceType(),
        },
        token ?? undefined
      );
    },
    // Don't retry on failure - analytics shouldn't block anything
    retry: false,
    // Don't throw errors - silently ignore failures
    onError: () => {
      // Silently ignore analytics errors
    },
  });

  // Store mutation.mutate in a ref for stable trackEvent reference
  // This prevents infinite loops in useEffect dependencies
  const mutateRef = useRef<typeof mutation.mutate | null>(null);

  useEffect(() => {
    mutateRef.current = mutation.mutate;
  }, [mutation.mutate]);

  const trackEvent = useCallback(
    (params: TrackEventParams) => {
      // Fire and forget - don't await
      if (mutateRef.current) {
        mutateRef.current(params);
      }

      // Update last page reference for referrer tracking
      if (params.eventType === AnalyticsEventType.PAGE_VIEW) {
        lastPageRef.current = params.pagePath || window.location.pathname;
      }
    },
    [] // Empty deps - mutateRef is stable
  );

  return trackEvent;
}

/**
 * Hook for automatic page view tracking.
 *
 * Call this in page components to automatically track page views.
 */
export function usePageView(pageName?: string) {
  const trackEvent = useTrackEvent();

  useEffect(() => {
    // Capture external referrer (different origin only)
    let externalReferrer: string | undefined;
    if (document.referrer) {
      try {
        const referrerUrl = new URL(document.referrer);
        if (referrerUrl.origin !== window.location.origin) {
          externalReferrer = document.referrer;
        }
      } catch {
        // Invalid URL, ignore
      }
    }

    const eventData: Record<string, unknown> = {};
    if (pageName) eventData.page_name = pageName;
    if (externalReferrer) eventData.external_referrer = externalReferrer;

    trackEvent({
      eventType: AnalyticsEventType.PAGE_VIEW,
      pagePath: window.location.pathname,
      eventData: Object.keys(eventData).length > 0 ? eventData : undefined,
    });
  }, [trackEvent, pageName]);
}

/**
 * Hook for tracking scorecard views.
 */
export function useTrackScorecardView(scorecardId: number | undefined) {
  const trackEvent = useTrackEvent();

  useEffect(() => {
    if (scorecardId && scorecardId > 0) {
      trackEvent({
        eventType: AnalyticsEventType.SCORECARD_VIEW,
        targetType: 'scorecard',
        targetId: scorecardId,
      });
    }
  }, [trackEvent, scorecardId]);
}

/**
 * Hook for tracking profile views.
 */
export function useTrackProfileView(userId: number | undefined) {
  const trackEvent = useTrackEvent();

  useEffect(() => {
    if (userId && userId > 0) {
      trackEvent({
        eventType: AnalyticsEventType.PROFILE_VIEW,
        targetType: 'user',
        targetId: userId,
      });
    }
  }, [trackEvent, userId]);
}

/**
 * Hook for tracking search events.
 */
export function useTrackSearch() {
  const trackEvent = useTrackEvent();

  return useCallback(
    (query: string, searchType: 'game' | 'user' | 'general', resultCount?: number) => {
      trackEvent({
        eventType: AnalyticsEventType.SEARCH,
        eventSubtype: searchType,
        eventData: {
          query,
          result_count: resultCount,
        },
      });
    },
    [trackEvent]
  );
}

// =============================================================================
// Dashboard Query Hook (Super-Admin Only)
// =============================================================================

export const analyticsKeys = {
  all: ['analytics'] as const,
  overview: () => [...analyticsKeys.all, 'overview'] as const,
};

/**
 * Hook for fetching analytics overview (super-admin only).
 */
export function useAnalyticsOverview() {
  const { getToken } = useAuth();

  return useQuery<AnalyticsOverviewResponse>({
    queryKey: analyticsKeys.overview(),
    queryFn: async () => {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      return mlbApi.getAnalyticsOverview(token);
    },
    staleTime: STALE_TIMES.LIVE_DATA,
    ...QUERY_RETRY_CONFIG,
  });
}
